<template>
  <div class="bootstrap-listbox border rounded d-flex flex-column" :class="{ 'opacity-50 pe-none': disabled }">
    <!-- Header Slot -->
    <div v-if="$slots.header" class="p-2 border-bottom">
      <slot name="header"></slot>
    </div>

    <!-- Selected Items (Multiple Mode) -->
    <div v-if="multiple && selectedOptions.length > 0" class="p-2 border-bottom d-flex flex-column align-items-start" style="gap: 0.25rem;">
        <div
          v-for="(option, index) in selectedOptions"
          :key="'sel_' + getOptionRenderKey(option, index)"
          class="badge badge-secondary d-flex align-items-center"
          style="font-size: 0.75em; padding: 0.15em 0.35em; border-radius: 0.25rem; background-color: #6c757d; color: #fff;"
        >
        <span class="text-truncate">{{ getOptionLabel(option) }}</span>
        <span
          class="ml-1"
          style="cursor: pointer; font-size: 1.1em; line-height: 1; margin-left: 0.5rem;"
          @click.stop="removeSelectedOption($event, option)"
        >&times;</span>
      </div>
    </div>

    <!-- Filter Header -->
    <div v-if="filter" class="p-2 border-bottom bg-light">
      <div class="position-relative">
        <input
          type="text"
          class="form-control form-control-sm pr-4"
          :placeholder="filterPlaceholder"
          v-model="filterValue"
          @input="onFilterChange"
        >
        <div class="position-absolute" style="right: 0.5rem top: 50% transform: translateY(-50%) pointer-events: none">
            <slot name="filtericon">
                <span class="text-muted">&#x1F50D;</span>
            </slot>
        </div>
      </div>
    </div>

    <!-- Options List -->
    <div class="overflow-auto flex-grow-1" :style="listStyle">
      <ul class="list-group list-group-flush m-0 p-0" role="listbox" :aria-multiselectable="multiple">
        <li
          v-if="filterValue && visibleOptions.length === 0"
          class="list-group-item text-muted text-center py-3"
        >
          <slot name="emptyfilter">{{ emptyFilterMessage }}</slot>
        </li>
        <li
          v-else-if="!filterValue && visibleOptions.length === 0"
          class="list-group-item text-muted text-center py-3"
        >
          <slot name="empty">{{ emptyMessage }}</slot>
        </li>

        <template v-else-if="optionGroupLabel">
          <template v-for="(optionGroup, groupIndex) in visibleOptions">
            <li
              :key="'group_' + groupIndex"
              class="list-group-item bg-light font-weight-bold text-muted px-3 py-2"
              role="group"
            >
              <slot name="optiongroup" :option="optionGroup" :index="groupIndex">
                {{ getOptionGroupLabel(optionGroup) }}
              </slot>
            </li>
            <li
              v-for="(option, index) in getOptionGroupChildren(optionGroup)"
              :key="'group_' + groupIndex + '_opt_' + getOptionRenderKey(option, index)"
              class="list-group-item list-group-item-action px-3 py-2 d-flex align-items-center justify-content-between"
              :class="{
                'active': isSelected(option),
                'disabled': isOptionDisabled(option)
              }"
              :style="isOptionDisabled(option) ? '' : 'cursor: pointer'"
              role="option"
              :aria-selected="isSelected(option)"
              tabindex="0"
              @click="onOptionSelect($event, option)"
              @touchend="onOptionTouchEnd()"
              @keydown="onOptionKeyDown($event, option)"
            >
              <div class="text-truncate">
                <slot name="option" :option="option" :index="index">
                  {{ getOptionLabel(option) }}
                </slot>
              </div>
              <span v-if="checkmark && isSelected(option)" class="d-flex align-items-center ml-2">
                <slot name="indicator" :option="option">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.86199 11.5948C4.78717 11.5923 4.71366 11.5745 4.64596 11.5424C4.57826 11.5103 4.5178 11.4645 4.46827 11.4078L0.64936 7.22591C0.547514 7.11585 0.491321 6.96924 0.493012 6.81648C0.494703 6.66373 0.554162 6.51842 0.658392 6.41071C0.762621 6.303 0.903823 6.24108 1.05374 6.23724C1.20365 6.2334 1.35032 6.28795 1.46461 6.38555L4.85764 10.0984L12.5292 1.41164C12.632 1.31215 12.7758 1.25624 12.9248 1.25585C13.0738 1.25547 13.218 1.31065 13.3214 1.40954C13.4248 1.50843 13.4795 1.6436 13.4754 1.79255C13.4713 1.9415 13.4089 2.07369 13.2998 2.16723L5.27581 11.4011C5.22681 11.4587 5.1666 11.5054 5.09886 11.5383C5.03112 11.5713 4.95725 11.5898 4.88164 11.5931L4.86199 11.5948Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
                </slot>
              </span>
            </li>
          </template>
        </template>

        <template v-else>
          <li
            v-for="(option, index) in visibleOptions"
            :key="'opt_' + getOptionRenderKey(option, index)"
            class="list-group-item list-group-item-action px-3 py-2 d-flex align-items-center justify-content-between"
            :class="{
              'active': isSelected(option),
              'disabled': isOptionDisabled(option)
            }"
            :style="isOptionDisabled(option) ? '' : 'cursor: pointer'"
            role="option"
            :aria-selected="isSelected(option)"
            tabindex="0"
            @click="onOptionSelect($event, option)"
            @touchend="onOptionTouchEnd()"
            @keydown="onOptionKeyDown($event, option)"
          >
            <div class="text-truncate">
              <slot name="option" :option="option" :index="index">
                {{ getOptionLabel(option) }}
              </slot>
            </div>
            <span v-if="checkmark && isSelected(option)" class="d-flex align-items-center ml-2">
              <slot name="indicator" :option="option">
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.86199 11.5948C4.78717 11.5923 4.71366 11.5745 4.64596 11.5424C4.57826 11.5103 4.5178 11.4645 4.46827 11.4078L0.64936 7.22591C0.547514 7.11585 0.491321 6.96924 0.493012 6.81648C0.494703 6.66373 0.554162 6.51842 0.658392 6.41071C0.762621 6.303 0.903823 6.24108 1.05374 6.23724C1.20365 6.2334 1.35032 6.28795 1.46461 6.38555L4.85764 10.0984L12.5292 1.41164C12.632 1.31215 12.7758 1.25624 12.9248 1.25585C13.0738 1.25547 13.218 1.31065 13.3214 1.40954C13.4248 1.50843 13.4795 1.6436 13.4754 1.79255C13.4713 1.9415 13.4089 2.07369 13.2998 2.16723L5.27581 11.4011C5.22681 11.4587 5.1666 11.5054 5.09886 11.5383C5.03112 11.5713 4.95725 11.5898 4.88164 11.5931L4.86199 11.5948Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
              </slot>
            </span>
          </li>
        </template>
      </ul>
    </div>

    <!-- Footer Slot -->
    <div v-if="$slots.footer" class="p-2 border-top">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultiselectListbox',
  props: {
    value: null,
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: [String, Function],
      default: null,
    },
    optionValue: {
      type: [String, Function],
      default: null,
    },
    optionDisabled: {
      type: [String, Function],
      default: null,
    },
    optionGroupLabel: {
      type: [String, Function],
      default: null,
    },
    optionGroupChildren: {
      type: [String, Function],
      default: null,
    },
    listStyle: {
      type: [Object, String],
      default: () => ({ maxHeight: '125px' }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dataKey: {
      type: String,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    metaKeySelection: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: Boolean,
      default: false,
    },
    filterPlaceholder: {
      type: String,
      default: 'Search...',
    },
    filterLocale: {
      type: String,
      default: undefined,
    },
    filterMatchMode: {
      type: String,
      default: 'contains',
    },
    filterFields: {
      type: Array,
      default: null,
    },
    emptyFilterMessage: {
      type: String,
      default: 'No results found',
    },
    emptyMessage: {
      type: String,
      default: 'No available options',
    },
    checkmark: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterValue: '',
      optionTouched: false,
    }
  },
  mounted() {
    this.$nextTick(() => {
      const selectedItem = this.$el.querySelector('.list-group-item.active')
      if (selectedItem) {
        const wrapper = this.$el.querySelector('.overflow-auto')
        if (wrapper) {
          const wrapperRect = wrapper.getBoundingClientRect()
          const itemRect = selectedItem.getBoundingClientRect()
          if (itemRect.top < wrapperRect.top || itemRect.bottom > wrapperRect.bottom) {
            wrapper.scrollTop += itemRect.top - wrapperRect.top
          }
        }
      }
    })
  },
  computed: {
    visibleOptions() {
      if (this.optionGroupLabel) {
        const visibleOptions = []
        for (const optgroup of this.options) {
          const children = this.getOptionGroupChildren(optgroup)
          let filteredChildren = []

          if (this.filterValue) {
            filteredChildren = this.filterOptions(children)
          } else {
            filteredChildren = children || []
          }

          if (filteredChildren.length > 0) {
            const groupCopy = { ...optgroup }

            if (typeof this.optionGroupChildren === 'string') {
              groupCopy[this.optionGroupChildren] = filteredChildren
            } else {
              groupCopy.__children = filteredChildren
            }
            visibleOptions.push(groupCopy)
          }
        }
        return visibleOptions
      } else {
        if (this.filterValue) {
          return this.filterOptions(this.options)
        } else {
          return this.options
        }
      }
    },
    equalityKey() {
      return this.optionValue ? null : this.dataKey
    },
    selectedOptions() {
      if (!this.multiple || !this.value || !this.value.length) return []
      const flatOptions = []
      if (this.optionGroupLabel) {
        for (const optgroup of this.options) {
          const children = this.getOptionGroupChildren(optgroup)
          if (children) flatOptions.push(...children)
        }
      } else {
        flatOptions.push(...(this.options || []))
      }
      return flatOptions.filter(opt => this.isSelected(opt))
    },
  },
  methods: {
    resolveFieldData(data, field) {
      if (data != null && field) {
        if (typeof field === 'function') {
          return field(data)
        } else if (typeof data === 'object') {
          if (field.indexOf('.') === -1) {
            return data[field]
          } else {
            const fields = field.split('.')
            let value = data
            for (let i = 0, len = fields.length; i < len; ++i) {
              if (value == null) {
                return null
              }
              value = value[fields[i]]
            }
            return value
          }
        }
      }
      return null
    },
    getOptionLabel(option) {
      if (option == null) return ''
      if (this.optionLabel) {
        const val = this.resolveFieldData(option, this.optionLabel)
        return val != null ? val : option
      }
      return option
    },
    getOptionValue(option) {
      if (option == null) return null
      if (this.optionValue) {
        return this.resolveFieldData(option, this.optionValue)
      }
      return option
    },
    getOptionRenderKey(option, index) {
      return this.dataKey ? this.resolveFieldData(option, this.dataKey) : index
    },
    isOptionDisabled(option) {
      if (this.optionDisabled) {
        return this.resolveFieldData(option, this.optionDisabled)
      }
      return false
    },
    getOptionGroupLabel(optionGroup) {
      return this.resolveFieldData(optionGroup, this.optionGroupLabel)
    },
    getOptionGroupChildren(optionGroup) {
      if (optionGroup.__children) return optionGroup.__children
      return this.resolveFieldData(optionGroup, this.optionGroupChildren)
    },
    filterOptions(options) {
      if (!options) return []
      const filterValue = this.filterValue.toLocaleLowerCase(this.filterLocale)
      return options.filter(option => {
        const fields = this.filterFields || (this.optionLabel ? [this.optionLabel] : null)

        if (!fields) {
          const stringValue = String(option).toLocaleLowerCase(this.filterLocale)
          return stringValue.indexOf(filterValue) !== -1
        }

        for (const field of fields) {
          const fieldValue = this.resolveFieldData(option, field)
          if (fieldValue != null) {
            const stringValue = String(fieldValue).toLocaleLowerCase(this.filterLocale)
            let match = false
            switch (this.filterMatchMode) {
              case 'contains':
                match = stringValue.indexOf(filterValue) !== -1
                break
              case 'startsWith':
                match = stringValue.startsWith(filterValue)
                break
              case 'endsWith':
                match = stringValue.endsWith(filterValue)
                break
              case 'equals':
                match = stringValue === filterValue
                break
              case 'notEquals':
                match = stringValue !== filterValue
                break
              default:
                match = stringValue.indexOf(filterValue) !== -1
                break
            }
            if (match) return true
          }
        }
        return false
      })
    },
    areValuesEqual(val1, val2) {
      if (this.equalityKey) {
        const key1 = val1 !== null && typeof val1 === 'object' ? this.resolveFieldData(val1, this.equalityKey) : val1
        const key2 = val2 !== null && typeof val2 === 'object' ? this.resolveFieldData(val2, this.equalityKey) : val2
        return key1 === key2
      }
      return val1 === val2
    },
    isSelected(option) {
      let selected = false
      const optionValue = this.getOptionValue(option)

      if (this.multiple) {
        if (this.value) {
          for (const val of this.value) {
            if (this.areValuesEqual(this.getOptionValue(val), optionValue)) {
              selected = true
              break
            }
          }
        }
      } else {
        if (Array.isArray(this.value)) {
          selected = this.areValuesEqual(this.getOptionValue(this.value[0]), optionValue)
        } else {
          selected = this.areValuesEqual(this.getOptionValue(this.value), optionValue)
        }
      }

      return selected
    },
    removeOption(option) {
      const optionValue = this.getOptionValue(option)
      return this.value.filter(val => !this.areValuesEqual(this.getOptionValue(val), optionValue))
    },
    removeSelectedOption(event, option) {
      if (this.disabled) return
      const value = this.removeOption(option)
      this.updateModel(event, value)
    },
    updateModel(event, value) {
      this.$emit('input', value)
      this.$emit('change', { originalEvent: event, value: value })
    },
    onOptionSelect(event, option) {
      if (this.disabled || this.isOptionDisabled(option)) {
        return
      }

      if (this.multiple) {
        this.onOptionSelectMultiple(event, option)
      } else {
        this.onOptionSelectSingle(event, option)
      }

      this.optionTouched = false
    },
    onOptionTouchEnd() {
      if (this.disabled) {
        return
      }
      this.optionTouched = true
    },
    onOptionSelectSingle(event, option) {
      const selected = this.isSelected(option)
      let valueChanged = false
      let value = null
      const metaSelection = this.optionTouched ? false : this.metaKeySelection

      if (metaSelection) {
        const metaKey = (event.metaKey || event.ctrlKey)

        if (selected) {
          if (metaKey) {
            value = null
            valueChanged = true
          }
        } else {
          value = this.getOptionValue(option)
          valueChanged = true
        }
      } else {
        value = selected ? null : this.getOptionValue(option)
        valueChanged = true
      }

      if (valueChanged) {
        this.updateModel(event, value)
      }
    },
    onOptionSelectMultiple(event, option) {
      const selected = this.isSelected(option)
      let valueChanged = false
      let value = null
      const metaSelection = this.optionTouched ? false : this.metaKeySelection

      if (metaSelection) {
        const metaKey = (event.metaKey || event.ctrlKey)

        if (selected) {
          if (metaKey) {
            value = this.removeOption(option)
          } else {
            value = [this.getOptionValue(option)]
          }
          valueChanged = true
        } else {
          value = metaKey ? (this.value || []) : []
          value = [...value, this.getOptionValue(option)]
          valueChanged = true
        }
      } else {
        if (selected) {
          value = this.removeOption(option)
        } else {
          value = [...(this.value || []), this.getOptionValue(option)]
        }
        valueChanged = true
      }

      if (valueChanged) {
        this.updateModel(event, value)
      }
    },
    onOptionKeyDown(event, option) {
      const item = event.currentTarget

      switch (event.which) {
        // down
        case 40: {
          const nextItem = this.findNextItem(item)
          if (nextItem) {
            nextItem.focus()
          }
          event.preventDefault()
          break
        }
        // up
        case 38: {
          const prevItem = this.findPrevItem(item)
          if (prevItem) {
            prevItem.focus()
          }
          event.preventDefault()
          break
        }
        // enter or space
        case 13:
        case 32: {
          this.onOptionSelect(event, option)
          event.preventDefault()
          break
        }
      }
    },
    findNextItem(item) {
      const nextItem = item.nextElementSibling

      if (nextItem) {
        // Skip disabled or group header items
        return (nextItem.classList.contains('disabled') || nextItem.classList.contains('bg-light'))
          ? this.findNextItem(nextItem)
          : nextItem
      } else {
        return null
      }
    },
    findPrevItem(item) {
      const prevItem = item.previousElementSibling

      if (prevItem) {
        return (prevItem.classList.contains('disabled') || prevItem.classList.contains('bg-light'))
          ? this.findPrevItem(prevItem)
          : prevItem
      } else {
        return null
      }
    },
    onFilterChange(event) {
      this.$emit('filter', { originalEvent: event, value: event.target.value })
    },
  },
}
</script>

<style scoped>
.bootstrap-listbox {
  display: flex;
  flex-direction: column;
}
.bootstrap-listbox .list-group-item.active {
  z-index: 2;
}
.bootstrap-listbox .list-group-item:focus {
  outline: 2px solid #80bdff;
  outline-offset: -2px;
}
/* Ensure the rounded corners are respected by the flush list group */
.bootstrap-listbox .list-group-flush:last-child .list-group-item:last-child {
  border-bottom-width: 0;
  border-bottom-right-radius: calc(.25rem - 1px);
  border-bottom-left-radius: calc(.25rem - 1px);
}
</style>
